
import type { ReactNode } from "react";

import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import FlagOutlinedIcon from "@mui/icons-material/FlagOutlined";
import FavoriteBorderRoundedIcon from "@mui/icons-material/FavoriteBorderRounded";
import HourglassTopRoundedIcon from "@mui/icons-material/HourglassTopRounded";

import { Box, Stack, Typography } from "@mui/material";

import {
  HOPE_SANS,
  HOPE_SERIF,
} from "@/shared/branding/thehopeonebrand";

import { formatImpactMoney } from "../utils/impactCurrency";

const c = {
  forestDeep: "#102C24",
  forest: "#1D4437",
  green: "#2F6B4F",
  sage: "#DCEADF",
  mist: "#EEF5EF",
  paper: "#FFFFFF",
  muted: "#6B7A73",
  border: "rgba(16,44,36,.10)",
};

type ImpactFundingOverviewProps = {
  goalAmount: number;
  committedAmount: number;
  verifiedAmount: number;
  contributorCount: number;

  /** Show or hide the full "모금 현황 상세" breakdown. */
  showDetailBreakdown?: boolean;
};

function FundingStat({
  label,
  amount,
  background,
}: {
  label: string;
  amount: number;
  background: string;
}) {
  return (
    <Box
      sx={{
        flex: 1,
        minWidth: 0,
        p: { xs: 1, sm: 2.25 },
        borderRadius: { xs: "14px", sm: "18px" },
        bgcolor: background,
      }}
    >
      <Typography
        sx={{
          fontFamily: HOPE_SANS,
          fontSize: {
            xs: "0.68rem",
            sm: "0.95rem",
          },
          fontWeight: 700,
          color: c.muted,
          mb: { xs: 0.35, sm: 0.75 },
        }}
      >
        {label}
      </Typography>

      <Typography
        sx={{
          fontFamily: HOPE_SERIF,
          fontWeight: 700,
          fontSize: {
            xs: "1rem",
            sm: "1.65rem",
          },
          lineHeight: 1.35,
          color: c.forestDeep,
          overflowWrap: "anywhere",
          fontVariantNumeric: "tabular-nums",
        }}
      >
        {formatImpactMoney(amount)}
      </Typography>
    </Box>
  );
}

function FundingRow({
  icon,
  label,
  description,
  amount,
  emphasized = false,
}: {
  icon: ReactNode;
  label: string;
  description?: string;
  amount: number;
  emphasized?: boolean;
}) {
  return (
    <Stack
      direction="row"
      alignItems="center"
      spacing={1.5}
      sx={{
        py: 1.75,
        borderBottom: `1px solid ${c.border}`,
        "&:last-of-type": {
          borderBottom: "none",
        },
      }}
    >
      <Box
        sx={{
          width: 44,
          height: 44,
          flexShrink: 0,
          display: "grid",
          placeItems: "center",
          borderRadius: "13px",
          bgcolor: emphasized ? c.sage : c.mist,
          color: c.green,
        }}
      >
        {icon}
      </Box>

      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          sx={{
            fontFamily: HOPE_SANS,
            fontWeight: 800,
            fontSize: {
              xs: "0.92rem",
              sm: "1.05rem",
            },
            color: c.forestDeep,
            lineHeight: 1.4,
          }}
        >
          {label}
        </Typography>

        {description ? (
          <Typography
            sx={{
              mt: 0.35,
              fontFamily: HOPE_SANS,
              fontSize: {
                xs: "0.78rem",
                sm: "0.85rem",
              },
              lineHeight: 1.55,
              color: c.muted,
            }}
          >
            {description}
          </Typography>
        ) : null}
      </Box>

      <Typography
        sx={{
          flexShrink: 0,
          fontFamily: HOPE_SERIF,
          fontWeight: 700,
          fontSize: {
            xs: "1.05rem",
            sm: "1.25rem",
          },
          color: c.forestDeep,
          textAlign: "right",
          fontVariantNumeric: "tabular-nums",
          overflowWrap: "anywhere",
          maxWidth: "45%",
        }}
      >
        {formatImpactMoney(amount)}
      </Typography>
    </Stack>
  );
}

export default function ImpactFundingOverview({
  goalAmount,
  committedAmount,
  verifiedAmount,
  contributorCount,
  showDetailBreakdown = true,
}: ImpactFundingOverviewProps) {
  const safeGoal = Math.max(0, goalAmount);
  const safeCommitted = Math.max(0, committedAmount);
  const safeVerified = Math.max(0, verifiedAmount);
  const safeContributorCount = Math.max(0, contributorCount);

  const remainingAmount = Math.max(
    0,
    safeGoal - safeVerified
  );

  const pendingAmount = Math.max(
    0,
    safeCommitted - safeVerified
  );

  const progress =
    safeGoal > 0
      ? Math.min(
          100,
          (safeVerified / safeGoal) * 100
        )
      : 0;

  return (
    <Stack
      spacing={{ xs: 1.15, sm: 3 }}
      sx={{
        width: "100%",
        minWidth: 0,
        pb: { xs: 0.5, sm: 2 },
      }}
    >
      {/* PAGE TITLE */}

      <Box sx={{ pb: 0.5 }}>
        <Typography
          component="h1"
          sx={{
            fontFamily: HOPE_SERIF,
            fontWeight: 800,
            fontSize: {
              xs: "1.35rem",
              sm: "2.6rem",
            },
            lineHeight: 1.25,
            letterSpacing: "-0.04em",
            color: c.forestDeep,
          }}
        >
          기부 현황
        </Typography>

        <Typography
          sx={{
            mt: { xs: 0.35, sm: 0.85 },
            fontFamily: HOPE_SANS,
            fontSize: {
              xs: "0.72rem",
              sm: "1.05rem",
            },
            lineHeight: { xs: 1.45, sm: 1.65 },
            color: c.muted,
          }}
        >
          기부 참여와 입금 확인 현황을 확인하세요.
        </Typography>
      </Box>

      {/* GOAL PROGRESS */}

      <Box
        sx={{
          p: { xs: 1, sm: 2.25 },
          borderRadius: { xs: "14px", sm: "18px" },
          border: `1px solid ${c.border}`,
          bgcolor: c.paper,
        }}
      >
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="flex-end"
          spacing={1}
          sx={{ mb: { xs: 0.75, sm: 1.5 } }}
        >
          <Typography
            sx={{
              fontFamily: HOPE_SANS,
              fontSize: {
                xs: "0.72rem",
                sm: "1.05rem",
              },
              fontWeight: 800,
              color: c.forestDeep,
            }}
          >
            목표 달성률
          </Typography>

          <Typography
            sx={{
              fontFamily: HOPE_SERIF,
              fontWeight: 800,
              fontSize: {
                xs: "1.15rem",
                sm: "1.85rem",
              },
              color: c.green,
              fontVariantNumeric: "tabular-nums",
            }}
          >
            {Math.round(progress)}%
          </Typography>
        </Stack>

        <Box
          role="progressbar"
          aria-label="모금 목표 달성률"
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={Math.round(progress)}
          sx={{
            height: { xs: 7, sm: 10 },
            borderRadius: "999px",
            overflow: "hidden",
            bgcolor: "rgba(16,44,36,.08)",
          }}
        >
          <Box
            sx={{
              height: "100%",
              width: `${progress}%`,
              minWidth: progress > 0 ? 4 : 0,
              borderRadius: "999px",
              background:
                "linear-gradient(90deg, #1D4437, #2F6B4F)",
            }}
          />
        </Box>

        {safeGoal > 0 ? (
          <Typography
            sx={{
              mt: { xs: 0.65, sm: 1.25 },
              fontFamily: HOPE_SANS,
              fontSize: {
                xs: "0.65rem",
                sm: "0.95rem",
              },
              lineHeight: { xs: 1.45, sm: 1.6 },
              color: c.muted,
            }}
          >
            목표 {formatImpactMoney(safeGoal)} · 인증{" "}
            {formatImpactMoney(safeVerified)}
          </Typography>
        ) : null}
      </Box>

      {/* MAIN FUNDING AMOUNTS */}

      <Stack
        direction="row"
        spacing={{ xs: 0.65, sm: 1.5 }}
      >
        <FundingStat
          label="전체 신청 금액"
          amount={safeCommitted}
          background={c.mist}
        />

        <FundingStat
          label="전체 인증 금액"
          amount={safeVerified}
          background={c.sage}
        />
      </Stack>

      {/* OPTIONAL DETAILED BREAKDOWN */}

      {showDetailBreakdown ? (
        <Box
          sx={{
            p: { xs: 1.75, sm: 2.5 },
            borderRadius: "20px",
            bgcolor: c.paper,
            border: `1px solid ${c.border}`,
          }}
        >
          <Typography
            component="h2"
            sx={{
              fontFamily: HOPE_SERIF,
              fontWeight: 700,
              fontSize: {
                xs: "1.4rem",
                sm: "1.7rem",
              },
              color: c.forestDeep,
              mb: 0.75,
            }}
          >
            모금 현황 상세
          </Typography>

          <FundingRow
            icon={
              <FlagOutlinedIcon
                sx={{ fontSize: 22 }}
              />
            }
            label="목표 금액"
            description="캠페인의 전체 모금 목표"
            amount={safeGoal}
          />

          <FundingRow
            icon={
              <FavoriteBorderRoundedIcon
                sx={{ fontSize: 22 }}
              />
            }
            label="약속된 기부금"
            description="참여자가 기부를 신청한 총금액"
            amount={safeCommitted}
          />

          <FundingRow
            icon={
              <CheckCircleRoundedIcon
                sx={{ fontSize: 22 }}
              />
            }
            label="입금 확인된 기부금"
            description="입금 확인 및 인증이 완료된 금액"
            amount={safeVerified}
            emphasized
          />

          {pendingAmount > 0 ? (
            <FundingRow
              icon={
                <HourglassTopRoundedIcon
                  sx={{ fontSize: 22 }}
                />
              }
              label="입금 확인 대기"
              description="약속 금액 중 아직 인증되지 않은 금액"
              amount={pendingAmount}
            />
          ) : null}

          <FundingRow
            icon={
              <FlagOutlinedIcon
                sx={{ fontSize: 22 }}
              />
            }
            label="목표까지 남은 금액"
            description="전체 목표에서 인증 금액을 제외한 금액"
            amount={remainingAmount}
            emphasized
          />
        </Box>
      ) : null}

      {/* PARTICIPATION COUNT */}

      <Box
        sx={{
          px: { xs: 1.75, sm: 2.25 },
          py: { xs: 1.75, sm: 2 },
          borderRadius: "18px",
          bgcolor: c.mist,
        }}
      >
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          spacing={1}
        >
          <Typography
            sx={{
              fontFamily: HOPE_SANS,
              fontSize: {
                xs: "0.9rem",
                sm: "1rem",
              },
              color: c.forestDeep,
              fontWeight: 800,
            }}
          >
            전체 기부 참여 건수
          </Typography>

          <Typography
            sx={{
              fontFamily: HOPE_SERIF,
              fontSize: {
                xs: "1.3rem",
                sm: "1.55rem",
              },
              fontWeight: 700,
              color: c.forestDeep,
              fontVariantNumeric: "tabular-nums",
            }}
          >
            {safeContributorCount.toLocaleString("ko-KR")}건
          </Typography>
        </Stack>
      </Box>
    </Stack>
  );
}